import { PortfolioContent } from '@/models/portfolio/portfolio-content';
import { PortfolioSubjectStat } from '@/models/stats/portfolio-subject';
import { PortfolioDomainStat } from '@/models/stats/portfolio-domain';
import { PortfolioTopicStat } from '@/models/stats/portfolio-topic';
import { PortfolioCompetencyStat } from '@/models/stats/portfolio-competency';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { sortByProperty } from '@/utils/utils';

export class PortfolioSerializer {

  static get instance() {
    return this.INSTANCE;
  }
  private static INSTANCE = new PortfolioSerializer();

  public serializePortfolioContents(portfolioContents: any, activityType= 'assessment') {
    const serializer = this;
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    let portfolioContentItems = portfolioContents.items ? portfolioContents.items.usageData : [];
    if (activityType === 'offline-activity') {
      portfolioContentItems = portfolioContentItems['oa.others'] || [];
    }
    const serializedPortfolioContents: PortfolioContent[] = portfolioContentItems.map((portfolioContent: any) => {
      const thumbnailUrl = portfolioContent.thumbnail ?
        contentCdnUrl + portfolioContent.thumbnail :
        DEFAULT_IMAGES_PATH[portfolioContent.type];
      const serializedPortfolioContent: PortfolioContent = {
        id: portfolioContent.id,
        title: portfolioContent.title,
        description: portfolioContent.learningObjective,
        questionCount: portfolioContent.questionCount || 0,
        resourceCount: portfolioContent.resourceCount || 0,
        taskCount: portfolioContent.taskCount || 0,
        score: portfolioContent.score || 0,
        sessionId: portfolioContent.sessionId,
        efficacy: portfolioContent.efficacy || serializer.getRandomREEfValue(),
        engagement: portfolioContent.engagement || serializer.getRandomREEfValue(),
        relevance: portfolioContent.relevance || serializer.getRandomREEfValue(),
        contentType: portfolioContent.type,
        subType: portfolioContent.subType,
        thumbnailUrl,
        activityTimestamp: portfolioContent.activityTimestamp,
        owner: portfolioContent.owner,
        status: portfolioContent.status,
        timespent: portfolioContent.timeSpent,
        updatedAt: portfolioContent.updatedAt,
      };
      return serializedPortfolioContent;
    });
    return serializedPortfolioContents;
  }

  public serializePortfolioDetails(payload: any) {
    const protfolioDetails = payload.items ? payload.items : {};
    if (protfolioDetails.usageData && protfolioDetails.usageData.length > 0) {
      const usageData = protfolioDetails.usageData;
      sortByProperty(usageData, 'updatedAt', 'DESC');
      const maxScoreData: any = usageData[0];
      protfolioDetails.selectedSessionId = maxScoreData.sessionId;
    }

    return protfolioDetails;
  }

  public serializePortfolioStatsAllFacets(portfolioStats: any) {
    const serializer = this;
    const learnerPortfolioStats = portfolioStats.learnerPortfolioStats || [];
    const serializedPortfolioStats: PortfolioSubjectStat[] = learnerPortfolioStats.map((subjectStat: any) => {
      return serializer.normalizeSubjectStat(subjectStat);
    });
    return serializedPortfolioStats;
  }

  public serializePortfolioStatsBySubject(portfolioStats: any) {
    const serializer = this;
    const learnerPortfolioStats = portfolioStats.learnerPortfolioStats || [];
    const serializedPortfolioStats: PortfolioDomainStat[] = learnerPortfolioStats.map((domainStat: any) => {
      return serializer.normalizeDomainStat(domainStat);
    });
    return serializedPortfolioStats;
  }

  public serializePortfolioStatsByDomain(portfolioStats: any) {
    const serializer = this;
    const learnerPortfolioStats = portfolioStats.learnerPortfolioStats || [];
    const serializedPortfolioStats: PortfolioTopicStat[] = learnerPortfolioStats.map((topicStat: any) => {
      return serializer.normalizeTopicStat(topicStat);
    });
    return serializedPortfolioStats;
  }

  public serializePortfolioStatsByTopic(portfolioStats: any) {
    const serializer = this;
    const learnerPortfolioStats = portfolioStats.learnerPortfolioStats || [];
    const serializedPortfolioStats: PortfolioCompetencyStat[] = learnerPortfolioStats.map((competencyStat: any) => {
      return serializer.normalizeCompetencyStat(competencyStat);
    });
    return serializedPortfolioStats;
  }


  public normalizeTopicStat(topicStat: any) {
    const normalizedTopicStat: PortfolioTopicStat = {
      assessmentCount: topicStat.assessmentCount,
      assessmentExternalCount: topicStat.assessmentExternalCount,
      collectionCount: topicStat.collectionCount,
      collectionExternalCount: topicStat.collectionExternalCount,
      topicCode: topicStat.topicCode,
      topicName: topicStat.topicName,
      topicSeq: topicStat.topicSeq,
      oaCount: topicStat.oaCount,
      totalCount: topicStat.assessmentCount +
        topicStat.assessmentExternalCount +
        topicStat.collectionCount +
        topicStat.collectionExternalCount +
        topicStat.oaCount,
    };
    return normalizedTopicStat;
  }

  public normalizeDomainStat(domainStat: PortfolioDomainStat) {
    const normalizedDomainStat: PortfolioDomainStat = {
      assessmentCount: domainStat.assessmentCount,
      assessmentExternalCount: domainStat.assessmentExternalCount,
      collectionCount: domainStat.collectionCount,
      collectionExternalCount: domainStat.collectionExternalCount,
      domainCode: domainStat.domainCode,
      domainName: domainStat.domainName,
      domainSeq: domainStat.domainSeq,
      oaCount: domainStat.oaCount,
      totalCount: domainStat.assessmentCount +
        domainStat.assessmentExternalCount +
        domainStat.collectionCount +
        domainStat.collectionExternalCount +
        domainStat.oaCount,
    };
    return normalizedDomainStat;
  }

  public normalizeCompetencyStat(competencyStat: PortfolioCompetencyStat) {
    const normalizedCompetencyStat: PortfolioCompetencyStat = {
      assessmentCount: competencyStat.assessmentCount,
      assessmentExternalCount: competencyStat.assessmentExternalCount,
      collectionCount: competencyStat.collectionCount,
      collectionExternalCount: competencyStat.collectionExternalCount,
      competencyCode: competencyStat.competencyCode,
      competencyName: competencyStat.competencyName,
      competencySeq: competencyStat.competencySeq,
      oaCount: competencyStat.oaCount,
      totalCount: competencyStat.assessmentCount +
        competencyStat.assessmentExternalCount +
        competencyStat.collectionCount +
        competencyStat.collectionExternalCount +
        competencyStat.oaCount,
    };
    return normalizedCompetencyStat;
  }

  public normalizePortfolioIdenitifier(payload: any) {
    const users = payload.users ? payload.users : [];
    const newUser = (user: any) => {
      const cdnUrls = sessionService.getCdnUrl();
      const userCdn = cdnUrls.user_cdn_url;
      const thumbnailUrl = user.thumbnail ?
        userCdn + user.thumbnail :
        `${window.location.origin}/` + DEFAULT_IMAGES_PATH.profile;
      return {
        code: user.code,
        email: user.email,
        id: user.id,
        lastName: user.last_name,
        username: user.username,
        status: user.status,
        tenantName: user.tenant_name,
        mode: user.mode,
        thumbnail: thumbnailUrl,
      };
    };
    return users.map(newUser);
  }

  private normalizeSubjectStat(subjectStat: any) {
    const normalizedSubjectStat: PortfolioSubjectStat = {
      subjectCode: subjectStat.subjectCode,
      subjectName: subjectStat.subjectName,
      classificationCode: subjectStat.classificationCode,
      classificationName: subjectStat.classificationName,
      subjectSeq: subjectStat.subjectSeq,
      classificationSeq: subjectStat.classificationSeq,
      collectionCount: subjectStat.collectionCount,
      assessmentCount: subjectStat.assessmentCount,
      assessmentExternalCount: subjectStat.assessmentExternalCount,
      collectionExternalCount: subjectStat.collectionExternalCount,
      oaCount: subjectStat.oaCount,
      totalCount: subjectStat.assessmentCount +
        subjectStat.assessmentExternalCount +
        subjectStat.collectionCount +
        subjectStat.collectionExternalCount +
        subjectStat.oaCount,
    };
    return normalizedSubjectStat;
  }

  private getRandomREEfValue() {
    return (Math.random() * (0.6 - 0.4) + 0.4).toFixed(2);
  }

}

export const portfolioSerializer = PortfolioSerializer.instance;
